import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Alert, Tooltip, IconButton, Collapse, TableSortLabel, TablePagination, Stack
} from '@mui/material';
import {
  DataUsage as DataUsageIcon,
  AttachMoney as AttachMoneyIcon,
  Functions as FunctionsIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FileDownload as FileDownloadIcon,
  Api as ApiIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../../services/api';

type Order = 'asc' | 'desc';

interface FeatureBreakdown {
  purpose: string;
  total_tokens: number;
  cost_usd: number;
  cost_inr: number;
}

const formatCost = (inr: number, usd: number) => `₹${inr?.toFixed(2) || '0.00'} ($${usd?.toFixed(2) || '0.00'})`;

const Row = ({ row, view }: { row: any; view: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {['company', 'user'].includes(view) && (
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        {view === 'company' && (
          <>
            <TableCell>{row.company_name}</TableCell>
            <TableCell align="right">{row.total_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.input_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.output_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">${row.cost_usd?.toFixed(2)}</TableCell>
            <TableCell align="right">₹{row.cost_inr?.toFixed(2)}</TableCell>
            <TableCell align="right">{row.call_count}</TableCell>
          </>
        )}
        {view === 'user' && (
          <>
            <TableCell>{row.user_name}</TableCell>
            <TableCell>{row.user_email}</TableCell>
            <TableCell>{row.user_type}</TableCell>
            <TableCell align="right">{row.total_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.input_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.output_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">${row.cost_usd?.toFixed(2)}</TableCell>
            <TableCell align="right">₹{row.cost_inr?.toFixed(2)}</TableCell>
          </>
        )}
        {view === 'interview' && (
          <>
            <TableCell>{row.company_name}</TableCell>
            <TableCell>{row.candidate_name}</TableCell>
            <TableCell>{row.job_role}</TableCell>
            <TableCell align="right">{row.total_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.input_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.output_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">${row.cost_usd?.toFixed(2)}</TableCell>
            <TableCell align="right">₹{row.cost_inr?.toFixed(2)}</TableCell>
            <TableCell>{new Date(row.timestamp).toLocaleString()}</TableCell>
          </>
        )}
        {view === 'feature' && (
          <>
            <TableCell>{row.purpose_display || row.purpose}</TableCell>
            <TableCell align="right">{row.total_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.input_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">{row.output_tokens?.toLocaleString()}</TableCell>
            <TableCell align="right">${row.cost_usd?.toFixed(2)}</TableCell>
            <TableCell align="right">₹{row.cost_inr?.toFixed(2)}</TableCell>
            <TableCell align="right">{row.percentage?.toFixed(1)}%</TableCell>
          </>
        )}
      </TableRow>
      {['company', 'user'].includes(view) && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Feature Breakdown
                </Typography>
                <Table size="small" aria-label="features">
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature</TableCell>
                      <TableCell align="right">Total Tokens</TableCell>
                      <TableCell align="right">Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(row.feature_breakdown || []).map((fb: FeatureBreakdown) => (
                      <TableRow key={fb.purpose}>
                        <TableCell component="th" scope="row">{fb.purpose}</TableCell>
                        <TableCell align="right">{fb.total_tokens?.toLocaleString()}</TableCell>
                        <TableCell align="right">{formatCost(fb.cost_inr, fb.cost_usd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

const TokenUsage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [costFormula, setCostFormula] = useState<any>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('total_tokens');
  const [search, setSearch] = useState('');

  const views = ['company', 'user', 'interview', 'feature'];
  const currentView = views[tabIndex];

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/super-admin/token-usage/detailed/?view=${currentView}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const response = await api.get(url);
      if (response.data.success) {
        setData(response.data.data.breakdown || []);
        setSummary(response.data.data.summary || null);
        setCostFormula(response.data.data.cost_formula || null);
      }
    } catch (err: any) {
      console.error('Error fetching token usage data:', err);
      setError(err.response?.data?.error || 'Failed to fetch token usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  const handleApplyFilter = () => {
    fetchData();
  };

  const handleQuickPreset = (preset: string) => {
    const today = new Date();
    let from = '';
    let to = today.toISOString().split('T')[0];

    if (preset === 'This Month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    } else if (preset === 'Last Month') {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
      to = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
    } else if (preset === 'Last 3 Months') {
      from = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()).toISOString().split('T')[0];
    } else if (preset === 'All Time') {
      from = '';
      to = '';
    }

    setDateFrom(from);
    setDateTo(to);
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredData = useMemo(() => {
    if (!search) return data;
    const lowerSearch = search.toLowerCase();
    return data.filter((row: any) => {
      if (currentView === 'company') return row.company_name?.toLowerCase().includes(lowerSearch);
      if (currentView === 'user') return row.user_name?.toLowerCase().includes(lowerSearch) || row.user_email?.toLowerCase().includes(lowerSearch);
      if (currentView === 'interview') return row.company_name?.toLowerCase().includes(lowerSearch) || row.candidate_name?.toLowerCase().includes(lowerSearch);
      if (currentView === 'feature') return row.purpose_display?.toLowerCase().includes(lowerSearch) || row.purpose?.toLowerCase().includes(lowerSearch);
      return true;
    });
  }, [data, search, currentView]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a: any, b: any) => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, order, orderBy]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const exportToCSV = () => {
    if (!sortedData || sortedData.length === 0) return;
    
    // Get headers
    let headers: string[] = [];
    if (currentView === 'company') headers = ['Company Name', 'Total Tokens', 'Input Tokens', 'Output Tokens', 'Cost USD', 'Cost INR', 'API Calls'];
    if (currentView === 'user') headers = ['User Name', 'Email', 'User Type', 'Total Tokens', 'Input Tokens', 'Output Tokens', 'Cost USD', 'Cost INR'];
    if (currentView === 'interview') headers = ['Company', 'Candidate', 'Job Role', 'Total Tokens', 'Input Tokens', 'Output Tokens', 'Cost USD', 'Cost INR', 'Date'];
    if (currentView === 'feature') headers = ['Feature', 'Total Tokens', 'Input Tokens', 'Output Tokens', 'Cost USD', 'Cost INR', 'Percentage'];

    const csvRows = [headers.join(',')];

    sortedData.forEach((row: any) => {
      let rowData: any[] = [];
      if (currentView === 'company') rowData = [row.company_name, row.total_tokens, row.input_tokens, row.output_tokens, row.cost_usd, row.cost_inr, row.call_count];
      if (currentView === 'user') rowData = [row.user_name, row.user_email, row.user_type, row.total_tokens, row.input_tokens, row.output_tokens, row.cost_usd, row.cost_inr];
      if (currentView === 'interview') rowData = [row.company_name, row.candidate_name, row.job_role, row.total_tokens, row.input_tokens, row.output_tokens, row.cost_usd, row.cost_inr, row.timestamp];
      if (currentView === 'feature') rowData = [row.purpose_display || row.purpose, row.total_tokens, row.input_tokens, row.output_tokens, row.cost_usd, row.cost_inr, row.percentage];
      
      const escapedRow = rowData.map(d => typeof d === 'string' ? `"${d.replace(/"/g, '""')}"` : d);
      csvRows.push(escapedRow.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `token_usage_${currentView}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getColumns = () => {
    if (currentView === 'company') return [
      { id: 'company_name', label: 'Company Name' },
      { id: 'total_tokens', label: 'Total Tokens', numeric: true },
      { id: 'input_tokens', label: 'Input Tokens', numeric: true },
      { id: 'output_tokens', label: 'Output Tokens', numeric: true },
      { id: 'cost_usd', label: 'Cost (USD)', numeric: true },
      { id: 'cost_inr', label: 'Cost (₹)', numeric: true },
      { id: 'call_count', label: 'API Calls', numeric: true },
    ];
    if (currentView === 'user') return [
      { id: 'user_name', label: 'User Name' },
      { id: 'user_email', label: 'Email' },
      { id: 'user_type', label: 'User Type' },
      { id: 'total_tokens', label: 'Total Tokens', numeric: true },
      { id: 'input_tokens', label: 'Input Tokens', numeric: true },
      { id: 'output_tokens', label: 'Output Tokens', numeric: true },
      { id: 'cost_usd', label: 'Cost (USD)', numeric: true },
      { id: 'cost_inr', label: 'Cost (₹)', numeric: true },
    ];
    if (currentView === 'interview') return [
      { id: 'company_name', label: 'Company' },
      { id: 'candidate_name', label: 'Candidate' },
      { id: 'job_role', label: 'Job Role' },
      { id: 'total_tokens', label: 'Total Tokens', numeric: true },
      { id: 'input_tokens', label: 'Input Tokens', numeric: true },
      { id: 'output_tokens', label: 'Output Tokens', numeric: true },
      { id: 'cost_usd', label: 'Cost (USD)', numeric: true },
      { id: 'cost_inr', label: 'Cost (₹)', numeric: true },
      { id: 'timestamp', label: 'Date' },
    ];
    if (currentView === 'feature') return [
      { id: 'purpose_display', label: 'Feature' },
      { id: 'total_tokens', label: 'Total Tokens', numeric: true },
      { id: 'input_tokens', label: 'Input Tokens', numeric: true },
      { id: 'output_tokens', label: 'Output Tokens', numeric: true },
      { id: 'cost_usd', label: 'Cost (USD)', numeric: true },
      { id: 'cost_inr', label: 'Cost (₹)', numeric: true },
      { id: 'percentage', label: '% of Total', numeric: true },
    ];
    return [];
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Token Usage & Costing
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Detailed breakdown of AI token consumption across the platform
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {summary ? summary.total_tokens?.toLocaleString() : <CircularProgress size={24} />}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Tokens</Typography>
              </Box>
              <DataUsageIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="secondary">
                  {summary ? summary.call_count?.toLocaleString() : <CircularProgress size={24} />}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total API Calls</Typography>
              </Box>
              <ApiIcon color="secondary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {summary ? `₹${summary.total_cost_inr?.toFixed(2)}` : <CircularProgress size={24} />}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summary ? `($${summary.total_cost_usd?.toFixed(2)})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Cost</Typography>
              </Box>
              <AttachMoneyIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Tooltip
                  title={
                    costFormula ? (
                      <Box sx={{ p: 1, whiteSpace: 'pre-line' }}>
                        <Typography variant="subtitle2" fontWeight="bold">Cost Formula:</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{costFormula.description}</Typography>
                        <Typography variant="subtitle2" fontWeight="bold">Model Rates:</Typography>
                        {Object.entries(costFormula.models).map(([model, rates]: [string, any]) => (
                          <Typography key={model} variant="body2">
                            • {model}: ₹{rates.input_rate_inr}/1K input, ₹{rates.output_rate_inr}/1K output
                          </Typography>
                        ))}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          USD to INR: ₹{costFormula.usd_to_inr_rate?.toFixed(2) || '85.00'}
                        </Typography>
                      </Box>
                    ) : 'Cost data unavailable'
                  }
                  arrow
                  placement="top"
                >
                  <Typography variant="h6" fontWeight="bold" color="info.main" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                    View Cost Formula
                  </Typography>
                </Tooltip>
                <Typography variant="body2" color="text.secondary">Pricing Information</Typography>
              </Box>
              <FunctionsIcon color="info" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button variant="contained" onClick={handleApplyFilter}>Apply</Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => handleQuickPreset('This Month')}>This Month</Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickPreset('Last Month')}>Last Month</Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickPreset('Last 3 Months')}>Last 3 Months</Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickPreset('All Time')}>All Time</Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabs and Data */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} aria-label="token usage tabs">
            <Tab label="Companies" />
            <Tab label="B2C Users" />
            <Tab label="Interviews" />
            <Tab label="By Feature" />
          </Tabs>
          <Stack direction="row" spacing={2} alignItems="center">
            {['company', 'user', 'interview'].includes(currentView) && (
              <TextField
                placeholder="Search..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" fontSize="small" sx={{ mr: 1 }} />
                }}
              />
            )}
            <Button startIcon={<FileDownloadIcon />} variant="outlined" onClick={exportToCSV}>
              Export CSV
            </Button>
          </Stack>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                {getColumns().map((col) => (
                  <TableCell key={col.id} align={col.numeric ? 'right' : 'left'}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={getColumns().length + 1} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={getColumns().length + 1} align="center" sx={{ py: 3 }}>
                    No data available
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row: any, idx: number) => (
                  <Row key={row.id || idx} row={row} view={currentView} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default TokenUsage;
